import RpcService  from '@/core/services/Rpc/RpcService';
import RpcResponse from '@/core/services/Rpc/RpcResponse';

import type SupplyOffer      from '@/domains/SupplyOffers/SupplyOffer';
import type Product          from '@/domains/Products/Product';
import type Partner          from '@/domains/Partners/Partner';
import type Manufacturer     from '@/domains/Manufacturers/Manufacturer';
import type RpcDataPagination from '@/core/services/Rpc/RpcDataPagination';
import type RpcDataSort       from '@/core/services/Rpc/RpcDataSort';

class SupplyOffersDAO {

    /**
     * Загружает одно разрешение по id
     */
    static async get(id: number): Promise<RpcResponse> {
        return await RpcService.makeRequestSilent('supplyOffers.get', {
            id,
        });
    }

    /**
     * Получить список разрешений
     */
    static async getList(
      filters?: {
        product?:      Product;
        seller?:       Partner;
        owner?:        Partner;
        manufacturer?: Manufacturer;
        currencyCode?: string;
        isApproved?:   boolean;
        isDeleted?:    boolean;
        statusId?:     string;
      },
      pagination?: RpcDataPagination,
      sort?: RpcDataSort,
    ): Promise<RpcResponse> {
        return await this.makeListRequest('supplyOffers.getList', filters, pagination, sort);
    }

    /**
     * Общая сборка параметров для getList и getListWithActualTariffs —
     * фильтры у них одинаковые, отличается только метод.
     */
    private static async makeListRequest(
      method: string,
      filters?: {
        product?:      Product;
        seller?:       Partner;
        owner?:        Partner;
        manufacturer?: Manufacturer;
        currencyCode?: string;
        isApproved?:   boolean;
        isDeleted?:    boolean;
        statusId?:     string;
      },
      pagination?: RpcDataPagination,
      sort?: RpcDataSort,
    ): Promise<RpcResponse> {
        let product;
        let seller;
        let owner;
        let manufacturer;

        if (filters?.product) {
            product = { id: filters.product.id };
        }
        else {
            product = undefined;
        }

        if (filters?.seller) {
            seller = { id: filters.seller.id };
        }
        else {
            seller = undefined;
        }

        if (filters?.owner) {
            owner = { id: filters.owner.id };
        }
        else {
            owner = undefined;
        }

        if (filters?.manufacturer) {
            manufacturer = { id: filters.manufacturer.id };
        }
        else {
            manufacturer = undefined;
        }

        return await RpcService.makeRequestSilent(
          method,
          {
            ...filters,
            product,
            seller,
            owner,
            manufacturer,
          },
          pagination,
          sort,
        );
    }

    /**
     * Тот же список, но с actual-тарифами: только действующие цены, история скрыта.
     * Нужен везде, где в таблице показываются цены.
     */
    static async getListWithActualTariffs(
      filters?: Parameters<typeof SupplyOffersDAO.getList>[0],
      pagination?: RpcDataPagination,
      sort?: RpcDataSort,
    ): Promise<RpcResponse> {
        return await this.makeListRequest('supplyOffers.getListWithActualTariffs', filters, pagination, sort);
    }

    /**
     * Создать разрешение
     */
    static async add(supplyOffer: SupplyOffer): Promise<RpcResponse> {
        return await RpcService.makeRequestSilent('supplyOffers.add', {
            supplyOffer,
        });
    }

    /**
     * Обновить разрешение
     */
    static async update(supplyOffer: SupplyOffer): Promise<RpcResponse> {
        return await RpcService.makeRequestSilent('supplyOffers.update', {
            supplyOffer,
        });
    }

    /**
     * Удалить разрешение
     */
    static async delete(supplyOffer: SupplyOffer, reasonText: string): Promise<RpcResponse> {
        return await RpcService.makeRequestSilent('supplyOffers.delete', {
            supplyOffer: { id: supplyOffer.id },
            reasonText,
        });
    }

    /**
     * Получить список статусов разрешений
     */
    static async getStatusesList(): Promise<RpcResponse> {
        return await RpcService.makeRequest('supplyOffers.getStatusesList');
    }

    /**
     * Получить список статусов доступности
     */
    static async getStatusesAvailabilityList(): Promise<RpcResponse> {
        return await RpcService.makeRequest('supplyOffers.getStatusesAvailabilityList');
    }
}

export default SupplyOffersDAO;